import type { MarketingTranslator } from '@/i18n/marketing'
/**
 * Content for the LC Franchise page (`/franchise`).
 *
 * Each section's structure is shaped to match the corresponding
 * page-specific component in `apps/lc/src/components/home/franchise/`.
 */

const IMG = '/marketing/franchise'

export function getFranchiseContent(mt: MarketingTranslator) {
  const franchiseContent = {
    // #01 Hero
    hero: {
      label: 'FRANCHISE  INFORMATION',
      body: mt('franchise.franchiseContent.hero.body'),
      emphasis: mt('franchise.franchiseContent.hero.emphasis'),
      imageUrl: `${IMG}/hero-banner.webp`,
      imageUrlMobile: `${IMG}/hero-banner-m.webp`,
      imageAlt: mt('franchise.franchiseContent.hero.imageAlt'),
    },

    // #02 Market data
    marketData: {
      headerLine1: mt('franchise.franchiseContent.marketData.headerLine1'),
      headerLine2: mt('franchise.franchiseContent.marketData.headerLine2'),
      titleLine1: mt('franchise.franchiseContent.marketData.titleLine1'),
      titleLine2: mt('franchise.franchiseContent.marketData.titleLine2'),
      body: mt('franchise.franchiseContent.marketData.body'),
      emphasis: mt('franchise.franchiseContent.marketData.emphasis'),
      chartUrl: `${IMG}/chart.webp`,
      chartUrlMobile: `${IMG}/chart-m.webp`,
      chartAlt: mt('franchise.franchiseContent.marketData.chartAlt'),
    },

    // #03 Parent concern
    parentConcern: {
      headerLine1: mt('franchise.franchiseContent.parentConcern.headerLine1'),
      headerLine2: mt('franchise.franchiseContent.parentConcern.headerLine2'),
      titleRows: [
        { plain: mt('franchise.franchiseContent.parentConcern.titleRows.0.plain'), highlight: mt('franchise.franchiseContent.parentConcern.titleRows.0.highlight') },
        { plain: mt('franchise.franchiseContent.parentConcern.titleRows.1.plain'), highlight: mt('franchise.franchiseContent.parentConcern.titleRows.1.highlight') },
      ],
      body: mt('franchise.franchiseContent.parentConcern.body'),
      emphasis: mt('franchise.franchiseContent.parentConcern.emphasis'),
      imageUrl: `${IMG}/review.webp`,
      imageUrlMobile: `${IMG}/review-m.webp`,
      imageAlt: mt('franchise.franchiseContent.parentConcern.imageAlt'),
    },

    // #04 Content power
    contentPower: {
      titleLine1: mt('franchise.franchiseContent.contentPower.titleLine1'),
      titleLine2Plain: mt('franchise.franchiseContent.contentPower.titleLine2Plain'),
      titleLine2Highlight: mt('franchise.franchiseContent.contentPower.titleLine2Highlight'),
      cards: [
        {
          caption: mt('franchise.franchiseContent.contentPower.cards.0.caption'),
          label: mt('franchise.franchiseContent.contentPower.cards.0.label'),
          value: mt('franchise.franchiseContent.contentPower.cards.0.value'),
          meta: mt('franchise.franchiseContent.contentPower.cards.0.meta'),
          iconUrl: `${IMG}/icon-online.svg`,
        },
        {
          caption: mt('franchise.franchiseContent.contentPower.cards.1.caption'),
          label: mt('franchise.franchiseContent.contentPower.cards.1.label'),
          value: mt('franchise.franchiseContent.contentPower.cards.1.value'),
          meta: mt('franchise.franchiseContent.contentPower.cards.1.meta'),
          iconUrl: `${IMG}/icon-youtube.svg`,
        },
        {
          caption: mt('franchise.franchiseContent.contentPower.cards.2.caption'),
          label: 'KBS·MBC·SBS',
          value: mt('franchise.franchiseContent.contentPower.cards.2.value'),
          meta: mt('franchise.franchiseContent.contentPower.cards.2.meta'),
          extraLine: mt('franchise.franchiseContent.contentPower.cards.2.extraLine'),
          iconUrl: `${IMG}/icon-broadcast.svg`,
        },
        {
          caption: mt('franchise.franchiseContent.contentPower.cards.3.caption'),
          label: mt('franchise.franchiseContent.contentPower.cards.3.label'),
          value: mt('franchise.franchiseContent.contentPower.cards.3.value'),
          meta: mt('franchise.franchiseContent.contentPower.cards.3.meta'),
          iconUrl: `${IMG}/icon-school.svg`,
        },
      ],
    },

    // #05 Queture marquee
    quetureMarquee: {
      bodyLine1: mt('franchise.franchiseContent.quetureMarquee.bodyLine1'),
      bodyLine2: mt('franchise.franchiseContent.quetureMarquee.bodyLine2'),
      emphasis: mt('franchise.franchiseContent.quetureMarquee.emphasis'),
      row1: Array.from({ length: 9 }, (_, i) => ({
        url: `${IMG}/queture-${String(i + 1).padStart(2, '0')}.webp`,
        alt: mt('media.thumbnail', { number: i + 1 }),
      })),
      row2: Array.from({ length: 9 }, (_, i) => ({
        url: `${IMG}/queture-${String(i + 10).padStart(2, '0')}.webp`,
        alt: mt('media.thumbnail', { number: i + 10 }),
      })),
    },

    // #06 System diagram
    systemDiagram: {
      titleLine1: mt('franchise.franchiseContent.systemDiagram.titleLine1'),
      titleLine2: mt('franchise.franchiseContent.systemDiagram.titleLine2'),
      bodyLine1: mt('franchise.franchiseContent.systemDiagram.bodyLine1'),
      bodyLine2: mt('franchise.franchiseContent.systemDiagram.bodyLine2'),
      emphasisLine1: mt('franchise.franchiseContent.systemDiagram.emphasisLine1'),
      emphasisLine2: mt('franchise.franchiseContent.systemDiagram.emphasisLine2'),
      ctaLabel: mt('franchise.franchiseContent.systemDiagram.ctaLabel'),
      imageUrl: `${IMG}/diagram.webp`,
      imageUrlMobile: `${IMG}/diagram-m.webp`,
      imageAlt: mt('franchise.franchiseContent.systemDiagram.imageAlt'),
    },

    // #07 Early-bird CTA banner
    earlyBird: {
      titleLine1: mt('franchise.franchiseContent.earlyBird.titleLine1'),
      titleLine2: mt('franchise.franchiseContent.earlyBird.titleLine2'),
      bodyLine1: mt('franchise.franchiseContent.earlyBird.bodyLine1'),
      bodyLine2: mt('franchise.franchiseContent.earlyBird.bodyLine2'),
      ctaLabel: mt('franchise.franchiseContent.earlyBird.ctaLabel'),
      imageUrl: `${IMG}/class-bg.webp`,
      imageAlt: mt('franchise.franchiseContent.earlyBird.imageAlt'),
    },

    // #09 6-step process
    process: {
      titleLine1: mt('franchise.franchiseContent.process.titleLine1'),
      titleLine2: mt('franchise.franchiseContent.process.titleLine2'),
      note: mt('franchise.franchiseContent.process.note'),
      steps: [
        {
          step: 'Step 1',
          name: mt('franchise.franchiseContent.process.steps.0.name'),
          description: mt('franchise.franchiseContent.process.steps.0.description'),
          iconUrl: `${IMG}/step-icon-01.svg`,
        },
        {
          step: 'Step 2',
          name: mt('franchise.franchiseContent.process.steps.1.name'),
          description: mt('franchise.franchiseContent.process.steps.1.description'),
          iconUrl: `${IMG}/step-icon-02.svg`,
        },
        {
          step: 'Step 3',
          name: mt('franchise.franchiseContent.process.steps.2.name'),
          description: mt('franchise.franchiseContent.process.steps.2.description'),
          iconUrl: `${IMG}/step-icon-03.svg`,
        },
        {
          step: 'Step 4',
          name: mt('franchise.franchiseContent.process.steps.3.name'),
          description: mt('franchise.franchiseContent.process.steps.3.description'),
          iconUrl: `${IMG}/step-icon-04.svg`,
        },
        {
          step: 'Step 5',
          name: mt('franchise.franchiseContent.process.steps.4.name'),
          description: mt('franchise.franchiseContent.process.steps.4.description'),
          iconUrl: `${IMG}/step-icon-05.svg`,
        },
        {
          step: 'Step 6',
          name: mt('franchise.franchiseContent.process.steps.5.name'),
          description: mt('franchise.franchiseContent.process.steps.5.description'),
          iconUrl: `${IMG}/step-icon-06.svg`,
        },
      ],
    },
  }
  return franchiseContent
}
